import type {
  GeneratedFormDefinition,
  GeneratedFormField,
} from '@template-ui/helpers/generated-form.helpers';
import { resolveWorkflowFormLayout, type WorkflowFormLayout } from './workflow-form-layout';
import { isWorkflowFormLookupBinding, type WorkflowFormLookupBinding } from './workflow-form-lookup-contract';

export interface WorkflowFormOption { value: string; label: string; }
export type WorkflowFormOptionSource = 'contract-options' | 'step-outcomes' | 'custom-options' | 'lookup';
export interface WorkflowFormFieldConfiguration {
  name: string;
  label?: string;
  required?: boolean;
  maxLength?: number;
  control?: 'text' | 'textarea' | 'select';
  optionSource?: WorkflowFormOptionSource;
  options?: WorkflowFormOption[];
  lookup?: WorkflowFormLookupBinding;
  layout?: WorkflowFormLayout | null;
}
export interface WorkflowFormConfiguration {
  version: 1 | 2 | 3;
  fields: WorkflowFormFieldConfiguration[];
}
export interface WorkflowFormConfigurationContext {
  outcomeField?: string;
  outcomes?: readonly { code: string; label: string }[];
  installedDefinition?: GeneratedFormDefinition;
  /** Installed runtime adapters may resolve authoritative offered choices; authoring readers keep them unavailable. */
  lookupRuntimeAvailable?: boolean;
}

const ownObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const allowedFieldKeys = new Set(['name', 'label', 'required', 'maxLength', 'control', 'optionSource', 'options', 'layout', 'lookup']);

/** Apply saved presentation/validation choices without weakening the installed command contract. */
export function applyWorkflowFormConfiguration(
  definition: GeneratedFormDefinition,
  configuration: unknown,
  context: WorkflowFormConfigurationContext = {},
): GeneratedFormDefinition | null {
  if (configuration === undefined || configuration === null) return definition;
  if (!ownObject(configuration) || ![1, 2, 3].includes(configuration['version'] as number) || !Array.isArray(configuration['fields']) || configuration['fields'].length > 50
    || Object.keys(configuration).some(key => key !== 'version' && key !== 'fields')) return null;
  const fields = [...definition.fields];
  const layouts = new Map<string, WorkflowFormLayout | null | undefined>();
  const configured = new Set<string>();
  for (const raw of configuration['fields']) {
    if (!ownObject(raw) || Object.keys(raw).some(key => !allowedFieldKeys.has(key))) return null;
    // Optional nullable properties are emitted by the server's persisted record projection.
    const item = Object.fromEntries(Object.entries(raw).filter(([key, value]) => key === 'name' || value !== null));
    const name = item['name'];
    if (typeof name !== 'string' || configured.has(name)) return null;
    configured.add(name);
    const index = fields.findIndex(field => field.name === name);
    if (index < 0) return null;
    const base = fields[index];
    const installed = context.installedDefinition?.fields.find(field => field.name === name) ?? base;
    if (item['layout'] != null && (configuration['version'] === 1 || installed.supportsLayout !== true)) return null;
    layouts.set(name, item['layout'] as WorkflowFormLayout | null | undefined);
    const next: GeneratedFormField = { ...base,
      errorCodes: [...base.errorCodes, `workflow.form.field.${name}`],
    };
    if ('label' in item) {
      if (typeof item['label'] !== 'string' || !item['label'].trim() || item['label'].length > 120) return null;
      Object.assign(next, { label: item['label'], labelToken: '' });
    }
    if ('required' in item) {
      if (typeof item['required'] !== 'boolean' || base.required && !item['required']) return null;
      Object.assign(next, { required: base.required || item['required'] });
    }
    if ('maxLength' in item) {
      const maximum = item['maxLength'];
      if (base.type !== 'string' || typeof maximum !== 'number' || !Number.isSafeInteger(maximum)
        || maximum < Math.max(1, base.minLength ?? 0) || maximum > 100000 || base.maxLength !== undefined && maximum > base.maxLength) return null;
      Object.assign(next, { maxLength: maximum });
    }
    const control = item['control'] ?? base.control;
    if ('control' in item && (base.type !== 'string' || !['text', 'textarea', 'select'].includes(String(control))
      || !['text', 'textarea', 'select'].includes(base.control))) return null;
    if (installed.options?.length && control !== 'select') return null;
    Object.assign(next, { control });

    const source = item['optionSource'] ?? (installed.control === 'select' ? 'contract-options' : undefined);
    if (source !== undefined && (control !== 'select' || !['contract-options', 'step-outcomes', 'custom-options', 'lookup'].includes(String(source)))) return null;
    if ('options' in item && source !== 'custom-options') return null;
    if ('lookup' in item && (configuration['version'] !== 3 || source !== 'lookup')) return null;
    if (source === 'lookup') {
      if (configuration['version'] !== 3 || installed.supportsLookup !== true || base.type !== 'string' || installed.type !== 'string'
        || installed.options?.length || !isWorkflowFormLookupBinding(item['lookup'])) return null;
      // A declaration does not provide authorized choices or runtime evidence.
      Object.assign(next, { options: [], lookupDomain: undefined, ...(context.lookupRuntimeAvailable === true ? {} : { disabled: true }) });
    }
    if (source === 'contract-options') {
      if (!installed.options?.length) return null;
      Object.assign(next, { options: installed.options });
    }
    if (source === 'step-outcomes') {
      if (name !== context.outcomeField || !installed.options?.length || !context.outcomes?.length
        || new Set(context.outcomes.map(outcome => outcome.code)).size !== context.outcomes.length
        || context.outcomes.some(outcome => !installed.options!.some(option => option.value === outcome.code))) return null;
      Object.assign(next, { options: context.outcomes.map(outcome => ({ value: outcome.code, label: outcome.label, labelToken: '' })) });
    }
    if (source === 'custom-options') {
      if (base.type !== 'string' || installed.options?.length || !Array.isArray(item['options']) || !item['options'].length || item['options'].length > 100) return null;
      const options: { value: string; label: string; labelToken: string }[] = [];
      for (const option of item['options']) {
        if (!ownObject(option) || Object.keys(option).some(key => key !== 'value' && key !== 'label')
          || typeof option['value'] !== 'string' || !option['value'].trim()
          || typeof option['label'] !== 'string' || !option['label'].trim() || option['label'].length > 120
          || options.some(existing => existing.value === option['value'])
          || option['value'].length < (next.minLength ?? 0)
          || option['value'].length > (next.maxLength ?? 4000)) return null;
        options.push({ value: option['value'], label: option['label'], labelToken: '' });
      }
      Object.assign(next, { options, lookupDomain: undefined });
    }
    if (control === 'select' && !next.options?.length && source !== 'lookup') return null;
    if (next.maxLength !== undefined && next.options?.some(option => option.value.length > next.maxLength!)) return null;
    fields[index] = next;
  }
  const resolved = resolveWorkflowFormLayout(configuration['version'] as number, fields.map(field => ({
    control: field.control, layout: layouts.get(field.name),
  })));
  if (!resolved) return null;
  return { ...definition, fields: configuration['version'] !== 1
    ? fields.map((field, index) => ({ ...field, ...resolved[index] })) : fields,
    ...(configuration['version'] !== 1 ? { stackOnNarrow: true } : {}),
  };
}
